import { type Question, QuestionType } from 'shared-domains';

import { isComprehensionChildQuestion, minComprehensionTimer } from '~/utils/ComprehensionQuestionUtils';
import Constants from '~/config/Constants';
import $i18n from '~/services/i18n';

/**
 * Meta question type is a question type which is part of questions array but not counted in questions count.
 * These questions contain different questions inside them and hold no value on their own
 */
const metaQuestionTypes = [QuestionType.COMPREHENSION, QuestionType.COMPREHENSION_VIDEO];

interface FilterFn {
  (question: Question): Question[],
}

// #region private-definitions

export const isChildQuestion = (question: Question) => isComprehensionChildQuestion(question);
export const isMetaQuestion = (question: Question) => metaQuestionTypes.includes(question.type);
export const isMetaQuestionType = (questionType: QuestionType) => metaQuestionTypes.includes(questionType);

export const getFilteredQuestions = (questionList: Question[], filterCallback: FilterFn) => questionList.filter(filterCallback);

/**
 * There are 2 types of questions. Meta questions, which are questions but they can't be answered, they are just holders of questions.
 * These questions are not added in counts while displaying the quiz.
 * The other type is the regular questions, which you can answer and which you can count. These are non meta questions, countable questions.
 */
export function getCountableQuestions(questionList: Question[]) {
  return sortQuestions(questionList.filter(({ type }) => !metaQuestionTypes.includes(type)));
}

export const getParentQuestions = (questionList: Question[]) => questionList.filter(question => !isChildQuestion(question));

export function filterMinTime(questionType: QuestionType, time: number) {
  const minTimeMap = {
    [Constants.QuestionTypes.COMPREHENSION]: minComprehensionTimer,
    [Constants.QuestionTypes.COMPREHENSION_VIDEO]: minComprehensionTimer,
  };
  const minTime = minTimeMap[questionType]?.time;

  if (minTime && time < minTime) {
    return {
      allowed: false,
      errorMessage: minTimeMap[questionType].errorMessage,
    };
  }

  return {
    allowed: true,
    errorMessage: '',
  };
}

export function getParentIndexExplanation(
  parentQuestion: Question,
  countableQuestions: Question[],
  parentQuestionTitle = 'Comprehension',
  childrenListGetter = (question: Question) => question.comprehensionQuestionIds,
) {
  const firstChildOrder = countableQuestions
    .findIndex((question: Question) => question.comprehensionQuestionId === parentQuestion._id) + 1;
  const lastChildOrder = countableQuestions
    .findLastIndex((question: Question) => question.comprehensionQuestionId === parentQuestion._id) + 1;

  const childrenQuestionsList = childrenListGetter(parentQuestion) || [];
  if (!childrenQuestionsList.length) {
    return '';
  }
  /**
   * index + 1
   */
  if (childrenQuestionsList.length === 1) {
    return $i18n('{$1} for question {$2}', [parentQuestionTitle, firstChildOrder]);
  }
  return $i18n('{$1} for question {$2} to {$3}', [parentQuestionTitle, firstChildOrder, lastChildOrder]);
}

// sorting is needed for video qt question to appear in correct order
export function sortQuestions(questions: Question[]) {
  return questions.sort((a, b) => {
    if (a.comprehensionQuestionId && a.comprehensionQuestionId === b.comprehensionQuestionId) {
      if (a.comprehensionTimestamp !== undefined && b.comprehensionTimestamp !== undefined)
      { return a.comprehensionTimestamp - b.comprehensionTimestamp; }
    }
    return 0;
  });
}
